import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import type { StageReadinessRow } from '@/hooks/useDviStageReadiness';
import { useDviComponentTrends } from '@/hooks/useDviComponentTrends';
import { pct, stageBadgeClass, stageLabel } from './stageColors';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';

function TrendSparkline({ component, condition }: { component: string; condition: string }) {
  const { data: trends = [] } = useDviComponentTrends(component, condition, 7);
  if (trends.length < 2) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  return (
    <div className="h-6 w-20">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trends}>
          <YAxis hide domain={[0, 1]} />
          <Line
            type="monotone"
            dataKey="component_accuracy"
            stroke="hsl(var(--primary))"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface Props {
  rows: StageReadinessRow[];
  onSelect: (row: StageReadinessRow) => void;
  stageFilter: string;
  minSamples: number;
}

function VelocityIcon({ v }: { v: number }) {
  if (v > 0.005) return <TrendingUp className="h-3 w-3 text-primary" />;
  if (v < -0.005) return <TrendingDown className="h-3 w-3 text-destructive" />;
  return <Minus className="h-3 w-3 text-muted-foreground" />;
}

export function ComponentConfidenceTable({ rows, onSelect, stageFilter, minSamples }: Props) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (minSamples && r.total_samples < minSamples) return false;
      if (stageFilter !== 'all' && r.component_stage !== stageFilter) return false;
      if (search && !`${r.component} ${r.condition_type}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, stageFilter, minSamples, search]);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="p-3 border-b border-border flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-foreground">Component Confidence</h3>
        <Input
          placeholder="Filter component or condition…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs h-8"
        />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Component / Condition</TableHead>
            <TableHead>Sample Strength</TableHead>
            <TableHead>Component</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Recommendation</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>7d</TableHead>
            <TableHead>30d Trend</TableHead>
            <TableHead>Auto Ready</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-8">
                No components match the current filters.
              </TableCell>
            </TableRow>
          )}
          {filtered.map((r) => {
            const ratio = r.total_samples > 0 ? r.distinct_findings_count / r.total_samples : 0;
            const repetitive = r.total_samples >= 10 && ratio < 0.3;
            return (
              <TableRow
                key={`${r.component}-${r.condition_type}`}
                className="cursor-pointer"
                onClick={() => onSelect(r)}
              >
                <TableCell>
                  <div className="font-medium text-foreground">{r.component}</div>
                  <div className="text-xs text-muted-foreground">{r.condition_type}</div>
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-sm">
                        <span className="font-medium">{r.distinct_findings_count}</span>
                        <span className="text-muted-foreground"> unique · {r.total_samples} total</span>
                        {repetitive && (
                          <Badge variant="outline" className="ml-2 text-[10px]">repetitive</Badge>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Multi-image findings: {r.multi_image_rollup_count}<br />
                      Recent 30d samples: {r.recent_30d_samples}
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{pct(r.component_accuracy)}</span>
                  <Badge variant="outline" className={`ml-2 text-[10px] ${stageBadgeClass(r.component_stage)}`}>
                    {stageLabel(r.component_stage)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{pct(r.severity_accuracy)}</span>
                  <Badge variant="outline" className={`ml-2 text-[10px] ${stageBadgeClass(r.severity_stage)}`}>
                    {stageLabel(r.severity_stage)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{pct(r.recommendation_accuracy)}</span>
                  <Badge variant="outline" className={`ml-2 text-[10px] ${stageBadgeClass(r.recommendation_stage)}`}>
                    {stageLabel(r.recommendation_stage)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={stageBadgeClass(r.component_stage)}>
                    {stageLabel(r.component_stage)}
                  </Badge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <TrendSparkline component={r.component} condition={r.condition_type} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <VelocityIcon v={r.velocity_30d} />
                    {r.velocity_30d >= 0 ? '+' : ''}{(r.velocity_30d * 100).toFixed(1)}%
                  </div>
                </TableCell>
                <TableCell>
                  {r.automation_ready ? (
                    <Badge className="bg-primary/15 text-primary border-primary/30" variant="outline">Ready</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">No</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
